import { useBackendApi } from './backendApi';
import { useEventsPublisher } from 'event-bus';
import { SetupArea, SetupAreaVersionStatus } from './setupAreas';

export interface CreateMarketRequest {
  name: string;
  state: string;
  timezone?: string;
}

export interface UpdateMarketRequest {
  name: string;
  alias: string,
  state: string;
  timezone?: string;
}


export interface CopyMarketRequest {
  name: string;
  state: string;
  description?: string;
  timezone?: string;
}

export interface ShortMarket {
  id: number;
  name: string;
  state: string;
  description?: string;
}

export interface Market {
  id: number;
  name: string;
  state: string;
  description?: string;
  setupArea: SetupArea;
}

export interface BuildMarketBasedOnTeamDataRequest {
  teamId: number,
  name: string,
  alias: string,
  version: string,
  state: string
  description?: string,
  timezone?: string,
  setsIds: number[]
}

export const MARKETS_UPDATED = 'MARKETS_UPDATED';

export function getSetupAreaVersionStatusLabel(status: SetupAreaVersionStatus) {
  switch (status) {
    case SetupAreaVersionStatus.DRAFT:
      return 'Draft';
    case SetupAreaVersionStatus.PUBLISHED:
      return 'Published';
    case SetupAreaVersionStatus.ARCHIVED:
      return 'Archived';
    default:
      return 'Unknown';
  }
}

export function getVersionStatusForMarket(setupArea: SetupArea): SetupAreaVersionStatus {
  if (setupArea.versions.length === 0) {
    return SetupAreaVersionStatus.DRAFT;
  }
  return setupArea.versions[0].status;
}

export function useMarketsAdminService() {
  const nekstApi = useBackendApi();

  const eventsPublisher = useEventsPublisher();

  const createMarket = async (request: CreateMarketRequest): Promise<Market> => {
    const result = await nekstApi.post('/manager/markets', request) as Market;

    eventsPublisher.publish(MARKETS_UPDATED, {});

    return result;
  };

  const updateMarket = async (id: number, request: UpdateMarketRequest): Promise<Market> => {
    const result = await nekstApi.put(`/manager/markets/${id}`, request) as Market;

    eventsPublisher.publish(MARKETS_UPDATED, {});

    return result;
  };

  const getMarkets = async (): Promise<Market[]> => {
    return await nekstApi.get('/manager/markets') as Market[];
  };

  const deleteMarket = async (id: number): Promise<void> => {
    await nekstApi.delete(`/manager/markets/${id}`);

    eventsPublisher.publish(MARKETS_UPDATED, {});
  };

  const getById = async (id: number): Promise<Market> => {
    return await nekstApi.get(`/manager/markets/${id}`) as Market;
  };

  const copyMarket = async (id: number, data: CopyMarketRequest): Promise<Market> => {
    const result = await nekstApi.post(`/manager/markets/${id}/copy`, data) as Market;

    eventsPublisher.publish(MARKETS_UPDATED, {});

    return result;
  }

  const buildMarketBasedOnTeamData = async (request: BuildMarketBasedOnTeamDataRequest): Promise<Market> => {
    const result = await nekstApi.post('/manager/markets/fromteamdata', request) as Market;

    eventsPublisher.publish(MARKETS_UPDATED, {});

    return result;
  }

  return {
    createMarket,
    updateMarket,
    getById,
    getMarkets,
    deleteMarket,
    copyMarket,
    buildMarketBasedOnTeamData,
  };
}

export function getAreaIdAndVersionId(market: Market) {
  const setupAreaId = market.setupArea.id;
  const version = market.setupArea.versions[0].version;

  return {
    setupAreaId,
    version
  };
}
